import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createLogger } from '../utils/logger';

/**
 * 门店可见性检测 Hook
 * 使用 Intersection Observer API 检测门店是否在视口内
 */
export interface UseStoreVisibilityParams {
  storeId: number | string;
  rootMargin?: string; // 提前加载的边距，默认 '100px'
  threshold?: number; // 可见性阈值，默认 0.1
}

export interface UseStoreVisibilityResult {
  isVisible: boolean; // 门店是否可见
  elementRef: (node: HTMLElement | null) => void; // 用于绑定 DOM 元素的 ref
}

/**
 * 单个门店的可见性检测 Hook
 */
export function useStoreVisibility(params: UseStoreVisibilityParams): UseStoreVisibilityResult {
  const { storeId, rootMargin = '100px', threshold = 0.1 } = params;
  const logger = useMemo(() => createLogger(`useStoreVisibility:${storeId}`), [storeId]);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // 设置元素引用
  const setElementRef = useCallback((node: HTMLElement | null) => {
    // 清理旧的 observer
    if (observerRef.current && elementRef.current) {
      observerRef.current.unobserve(elementRef.current);
      observerRef.current.disconnect();
    }

    elementRef.current = node;

    if (node) {
      // 创建新的 observer
      const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
              const visible = entry.isIntersecting;
              setIsVisible(visible);
              logger.debug(`门店 ${visible ? '进入' : '离开'}视口`);
            });
        },
        {
          root: null, // 使用视口作为根
          rootMargin,
          threshold,
        }
      );

      observer.observe(node);
      observerRef.current = observer;

      // 初始检查
      setIsVisible(node.getBoundingClientRect().top < window.innerHeight + 100);
    }
  }, [logger, rootMargin, threshold]);

  // 清理
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    isVisible,
    elementRef: setElementRef,
  };
}

/**
 * 批量门店可见性管理 Hook
 * 用于管理多个门店的可见性状态
 */
export interface UseStoreVisibilityManagerParams {
  storeIds: (number | string)[];
  rootMargin?: string;
  threshold?: number;
  onStoreVisible?: (storeId: number | string) => void; // 门店进入可见范围时的回调
}

export interface UseStoreVisibilityManagerResult {
  visibleStoreIds: Set<number | string>; // 当前可见的门店ID集合
  isStoreVisible: (storeId: number | string) => boolean; // 检查门店是否可见
  registerStoreElement: (storeId: number | string, element: HTMLElement | null) => void; // 注册门店元素
}

/**
 * 批量门店可见性管理 Hook
 */
export function useStoreVisibilityManager(
  params: UseStoreVisibilityManagerParams
): UseStoreVisibilityManagerResult {
  const { rootMargin = '100px', threshold = 0.1, onStoreVisible } = params;
  const [visibleStoreIds, setVisibleStoreIds] = useState<Set<number | string>>(new Set());
  const elementsRef = useRef<Map<number | string, HTMLElement>>(new Map());
  const observersRef = useRef<Map<number | string, IntersectionObserver>>(new Map());

  /** 页面 unmount 时置为 false，避免 setTimeout 回调里继续 setState */
  const mountedRef = useRef(true);

  // 使用 ref 存储 visibleStoreIds，避免在 callback 中依赖它
  const visibleStoreIdsRef = useRef<Set<number | string>>(visibleStoreIds);
  
  // 同步 ref
  useEffect(() => {
    visibleStoreIdsRef.current = visibleStoreIds;
  }, [visibleStoreIds]);

  // 注册门店元素
  // 注意：不依赖 visibleStoreIds，使用 ref 来访问最新值，避免死循环
  const registerStoreElement = useCallback(
    (storeId: number | string, element: HTMLElement | null) => {
      // 如果元素没有变化，不重新注册（避免重复创建 observer）
      const existingElement = elementsRef.current.get(storeId);
      if (existingElement === element) {
        return; // 元素没有变化，不需要重新注册
      }

      // 清理旧的 observer
      const oldObserver = observersRef.current.get(storeId);
      if (oldObserver) {
        oldObserver.disconnect();
        observersRef.current.delete(storeId);
      }

      // 移除旧的元素引用
      elementsRef.current.delete(storeId);

      if (element) {
        // 添加新的元素引用
        elementsRef.current.set(storeId, element);

        // 创建新的 observer
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const visible = entry.isIntersecting;
              setVisibleStoreIds((prev) => {
                // 检查是否需要更新（避免不必要的状态更新）
                const hasStore = prev.has(storeId);
                if (visible === hasStore) {
                  // 状态没有变化，返回原 Set（保持引用不变）
                  return prev;
                }
                // 状态有变化，创建新 Set
                const next = new Set(prev);
                if (visible) {
                  next.add(storeId);
                  // 门店进入可见范围，触发回调
                  if (onStoreVisible) {
                    onStoreVisible(storeId);
                  }
                } else {
                  next.delete(storeId);
                }
                return next;
              });
            });
          },
          {
            root: null,
            rootMargin,
            threshold,
          }
        );

        observer.observe(element);
        observersRef.current.set(storeId, observer);

        // 初始检查（使用 setTimeout 避免在注册时立即触发状态更新）
        setTimeout(() => {
          if (!mountedRef.current) return;
          const rect = element.getBoundingClientRect();
          const marginValue = Number.parseInt(rootMargin.replace('px', ''), 10) || 0;
          const initiallyVisible =
            rect.top < window.innerHeight + marginValue &&
            rect.bottom > -marginValue;
          if (initiallyVisible) {
            setVisibleStoreIds((prev) => {
              // 如果已经存在，不更新（保持引用不变）
              if (prev.has(storeId)) {
                return prev;
              }
              return new Set([...prev, storeId]);
            });
          }
        }, 0);
      }
    },
    [rootMargin, threshold, onStoreVisible] // 添加 onStoreVisible 依赖
  );

  // 检查门店是否可见
  const isStoreVisible = useCallback(
    (storeId: number | string) => {
      return visibleStoreIds.has(storeId);
    },
    [visibleStoreIds]
  );

  // 清理所有 observers，页面切走时不再更新可见性状态
  useEffect(() => {
    const observers = observersRef.current;
    const elements = elementsRef.current;
    return () => {
      mountedRef.current = false;
      observers.forEach((observer) => {
        observer.disconnect();
      });
      observers.clear();
      elements.clear();
    };
  }, []);

  return {
    visibleStoreIds,
    isStoreVisible,
    registerStoreElement,
  };
}
